import React, { useState, useRef } from "react";
import { Database, Download, Upload, AlertTriangle, CheckCircle2, FileWarning } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

/**
 * Migração de Dados (Preview ↔ Produção)
 *
 * Permite exportar toda a BD num ficheiro .json.gz e re-importar noutro
 * ambiente (tipicamente Preview → Produção). Endpoints:
 *   GET  /api/admin/db/export
 *   POST /api/admin/db/import?confirm=yes-replace-everything
 */
export default function AdminMigration() {
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const fileRef = useRef(null);
  const [chosenFile, setChosenFile] = useState(null);

  const doExport = async () => {
    setExporting(true);
    try {
      // Use api (axios) — it injects the auth token via interceptor and
      // we ask for a Blob response so the gzipped binary is preserved.
      const res = await api.get("/admin/db/export", { responseType: "blob" });
      const blob = res.data;
      // Trigger download with filename from Content-Disposition if present
      const dispo = res.headers["content-disposition"] || "";
      const m = dispo.match(/filename="?([^";]+)"?/);
      const filename = m ? m[1] : `supertmatik-dump-${Date.now()}.json.gz`;
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(link.href);
      toast.success(`Exportado (${(blob.size / 1024 / 1024).toFixed(2)} MB)`);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Erro desconhecido";
      toast.error(`Erro ao exportar: ${detail}`);
    } finally { setExporting(false); }
  };

  const doImport = async () => {
    if (!chosenFile) { toast.error("Escolhe um ficheiro primeiro."); return; }
    if (confirmText !== "SUBSTITUIR") {
      toast.error('Tens de escrever "SUBSTITUIR" para confirmar.');
      return;
    }
    setImporting(true);
    setImportResult(null);
    try {
      const form = new FormData();
      form.append("file", chosenFile);
      const { data } = await api.post(
        "/admin/db/import?confirm=yes-replace-everything",
        form,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setImportResult(data);
      toast.success(`Importadas ${data.total_documents} documentos em ${Object.keys(data.collections_imported).length} coleções.`);
      setChosenFile(null);
      setConfirmText("");
      if (fileRef.current) fileRef.current.value = "";
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      toast.error(`Erro: ${detail}`);
    } finally { setImporting(false); }
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="admin-migration">
      <header className="flex items-center gap-3">
        <Database className="w-7 h-7 text-sky-600" />
        <div>
          <h1 className="text-2xl font-[Fredoka] font-bold text-slate-900">Migração de Dados</h1>
          <p className="text-sm text-slate-500">Copia toda a BD entre ambientes (ex: Preview → Produção).</p>
        </div>
      </header>

      {/* EXPORT */}
      <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-3">
        <div className="flex items-center gap-2">
          <Download className="w-5 h-5 text-emerald-600" />
          <h2 className="text-lg font-bold text-slate-900">1. Exportar BD do ambiente atual</h2>
        </div>
        <p className="text-sm text-slate-600">
          Descarrega um ficheiro <code className="bg-slate-100 px-1 rounded">.json.gz</code> com todas as coleções da BD deste ambiente.
          Inclui jogos, perguntas, robôs, utilizadores, resultados, configurações, etc.
        </p>
        <Button
          onClick={doExport}
          disabled={exporting}
          className="bg-emerald-600 hover:bg-emerald-700 rounded-full"
          data-testid="db-export-btn"
        >
          <Download className="w-4 h-4 mr-2" />
          {exporting ? "A exportar…" : "Exportar BD"}
        </Button>
      </section>

      {/* IMPORT */}
      <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-3">
        <div className="flex items-center gap-2">
          <Upload className="w-5 h-5 text-rose-600" />
          <h2 className="text-lg font-bold text-slate-900">2. Importar BD para o ambiente atual</h2>
        </div>

        <div className="rounded-lg bg-rose-50 border border-rose-200 p-3 flex items-start gap-2">
          <AlertTriangle className="w-5 h-5 text-rose-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-rose-800">
            <strong>Atenção:</strong> esta operação <strong>substitui completamente</strong> os dados da BD atual.
            Cada coleção presente no ficheiro é apagada e re-inserida. Faz sempre um export do ambiente atual <em>antes</em> como backup.
          </div>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-bold text-slate-700">Ficheiro <code className="text-xs">.json.gz</code></label>
          <input
            ref={fileRef}
            type="file"
            accept=".gz,.json,application/gzip,application/json"
            onChange={(e) => setChosenFile(e.target.files?.[0] || null)}
            className="w-full text-sm border border-slate-300 rounded-lg p-2"
            data-testid="db-import-file"
          />
          {chosenFile && (
            <div className="text-xs text-slate-500">
              Selecionado: <code>{chosenFile.name}</code> ({(chosenFile.size / 1024 / 1024).toFixed(2)} MB)
            </div>
          )}
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-bold text-slate-700">
            Escreve <code className="bg-rose-100 px-1 rounded">SUBSTITUIR</code> para confirmar
          </label>
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="SUBSTITUIR"
            className="w-full border border-slate-300 rounded-lg p-2 text-sm font-mono"
            data-testid="db-import-confirm"
          />
        </div>

        <Button
          onClick={doImport}
          disabled={importing || !chosenFile || confirmText !== "SUBSTITUIR"}
          className="bg-rose-600 hover:bg-rose-700 rounded-full"
          data-testid="db-import-btn"
        >
          <Upload className="w-4 h-4 mr-2" />
          {importing ? "A importar…" : "Substituir BD com este ficheiro"}
        </Button>

        {importResult && (
          <div className="mt-4 rounded-lg bg-emerald-50 border border-emerald-200 p-4">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              <h3 className="font-bold text-emerald-900">Importação concluída</h3>
            </div>
            <ul className="text-sm text-emerald-800 space-y-0.5">
              <li><strong>{importResult.total_documents}</strong> documentos importados</li>
              <li>BD origem: <code>{importResult.source_db_name}</code> ({importResult.source_exported_at})</li>
              <li>BD destino: <code>{importResult.db_name}</code></li>
            </ul>
            {importResult.errors?.length > 0 && (
              <div className="mt-2 text-xs text-rose-700 flex items-start gap-1">
                <FileWarning className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <div>Erros: {importResult.errors.join("; ")}</div>
              </div>
            )}
            <details className="mt-2 text-xs">
              <summary className="cursor-pointer text-emerald-700 hover:underline">
                Ver detalhe das {Object.keys(importResult.collections_imported).length} coleções
              </summary>
              <ul className="mt-1 grid grid-cols-2 gap-x-4">
                {Object.entries(importResult.collections_imported).map(([name, count]) => (
                  <li key={name} className="font-mono">{name}: {count}</li>
                ))}
              </ul>
            </details>
          </div>
        )}
      </section>

      {/* HOW-TO */}
      <section className="bg-slate-50 rounded-2xl border border-slate-200 p-6 text-sm text-slate-700">
        <h3 className="font-bold mb-2">Fluxo típico Preview → Produção</h3>
        <ol className="list-decimal pl-5 space-y-1">
          <li>Faz login no admin <strong>preview</strong> (este ambiente) e clica <em>Exportar BD</em>.</li>
          <li>Guarda o ficheiro <code>.json.gz</code> descarregado.</li>
          <li>Faz login no admin <strong>produção</strong> (https://eduplay-competitions.emergent.host/admin).</li>
          <li>Vai a <em>Migração de Dados</em>, escolhe o ficheiro e escreve <code>SUBSTITUIR</code>.</li>
          <li>Clica <em>Substituir BD</em> e aguarda. Confirma os resultados.</li>
        </ol>
      </section>
    </div>
  );
}
